/**
 * The seams between Paper's workspace shell and Settings.
 *
 * Paper's shell pieces (the theme toggle, the profile panel's appearance
 * rows, the accent, the rail, ⌘K) keep no data and make no API call. Each
 * reads the app through an adapter, and this file is where Settings supplies
 * them, so the shell drives Settings' own services and never a second copy
 * (One Paper A4; ricoh-paper docs/workspace-shell.md, docs/accent.md).
 */
import { inject, type Provider } from '@angular/core';
import { providePaperAccentStorage, type ThemeName as PaperThemeName } from '@ricoh-paper/angular';
import {
  paperLocalCommandRecents,
  paperLocalRailState,
  paperLocalThemeMemory,
  providePaperCommandRecents,
  providePaperThemeAdapter,
  providePaperThemeMemory,
  providePaperWorkspaceRailState,
} from '@ricoh-paper/angular/shell';
import { providePaperPanelSurface } from '@ricoh-paper/angular/panels';

import { ThemeService } from '../core/services/theme.service';
import { UserPreferencesService } from '../core/services/user-preferences.service';

/** Where the theme toggle remembers the last light and dark papers. */
export const WS_THEME_MEMORY_KEY = 'ws-theme-remembered';
/** Where the side nav remembers whether it is folded (`stateKey` is appended). */
export const WS_RAIL_STATE_PREFIX = 'ws-workspace-rail:';
/** Where ⌘K keeps its Recent list. */
export const WS_COMMAND_RECENTS_KEY = 'ws-command-recents';

export const paperShellProviders: Provider[] = [
  // The toggle and the profile panel's switch write Settings' ThemeService,
  // which persists the paper, writes data-paper-theme and is synced to the
  // profile by ProfileSettingsSyncService. A paper Settings does not offer
  // (Paper's legacy `current-*` aliases) is ignored there.
  providePaperThemeAdapter(() => {
    const themes = inject(ThemeService);
    return {
      theme: themes.theme,
      setTheme: (name: PaperThemeName) => themes.setTheme(name),
    };
  }),
  providePaperThemeMemory(() => paperLocalThemeMemory(WS_THEME_MEMORY_KEY)),
  // The accent (Ink, a colour, the division) is kept where the employee app
  // keeps it: `brandColorOverride` in UserPreferences, which
  // ProfileSettingsSyncService hydrates from the viewer and writes back to
  // the profile. `brandColorOverride()` is a signal, so the accent follows
  // the hydration that lands after boot.
  providePaperAccentStorage(() => {
    const prefs = inject(UserPreferencesService);
    return {
      read: () => prefs.brandColorOverride(),
      write: (value: string) => prefs.update({ brandColorOverride: value }),
    };
  }),
  providePaperWorkspaceRailState(() => paperLocalRailState(WS_RAIL_STATE_PREFIX)),
  providePaperCommandRecents(() => paperLocalCommandRecents(WS_COMMAND_RECENTS_KEY)),
  // A push panel belongs to the page that opened it: a different path is a
  // different surface, and the panel closes (query changes such as a filter
  // do not count). Paper's default is the first path segment, which in
  // Settings is a whole rail group.
  providePaperPanelSurface(wsPanelSurface),
];

/** The panel surface: the path, without its query or fragment. */
export function wsPanelSurface(url: string): string {
  return url.split(/[?#]/)[0] || '/';
}
